import React, { useState } from "react";
import { StyleSheet, Platform, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { toggleShiftHandoverOption } from "../../store/slices/shiftHandOverSlice";
import LHSectionHeader from "../../components/LHSectionHeader";
import LHOptionCard from "../../components/LHOptionCard";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import ShiftHandoverActivityImageUpload from "./ShiftHandoverActivityImageUpload";
import { getCategoryColor } from "../../utils/categoryColors";
import { useTheme } from "../../theme";

interface Option {
  id: string;
  questionText: string;
  category: string;
  material?: string; // Add material field for PDF link
  details?: string; // Add details field for the flip functionality
  Pallet_Type?: string; // Add Pallet_Type field for the second label
}

interface ShiftHandOverActivityProps {
  title: string;
  options: Option[];
  category: string;
  headerBackgroundColor?: string;
  forceDisabled?: boolean;
}

const ShiftHandOverActivity: React.FC<ShiftHandOverActivityProps> = ({
  title,
  options,
  category,
  headerBackgroundColor,
  forceDisabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const selectedOptions = useSelector(
    (state: RootState) => state.shiftHandOver.selectedQuestions
  );
  const handoverState = useSelector(
    (state: RootState) => state.shiftHandOver.handoverState
  );
  const shiftHandoverImages = useSelector(
    (state: RootState) => state.shiftHandOver.shiftHandoverImages
  );

  // State for image upload modal
  const [imageUploadModalVisible, setImageUploadModalVisible] =
    useState<boolean>(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Option | null>(null);

  const handleToggleOption = (option: Option): void => {
    if (handoverState === "completed" || forceDisabled) return;
    dispatch(
      toggleShiftHandoverOption({
        id: option.id,
        questionText: option.questionText,
        category: option.category,
      })
    );
  };

  // Handle camera icon press to open image upload modal
  const handleCameraPress = (option: Option): void => {
    // Set the selected question and open the modal
    setSelectedQuestion(option);
    setImageUploadModalVisible(true);
  };

  const handleCloseModal = (): void => {
    setImageUploadModalVisible(false);
  };

  // Check if a question has images
  const hasImages = (questionId: string): boolean => {
    return shiftHandoverImages.some((img) => img.questionId === questionId);
  };
  return (
    <View
      style={[
        styles.cardOuterContainer,
        isDarkMode && { backgroundColor: colors.background.page },
      ]}
    >
      <View
        style={[
          styles.cardContainer,
          isDarkMode && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
            borderWidth: 1,
          },
        ]}
      >
        <LHSectionHeader
          title={title}
          containerStyle={{
            ...styles.sectionHeader,
            backgroundColor: headerBackgroundColor || getCategoryColor(category),
            // Card header rounded corners
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
          textStyle={styles.header1text}
          // icon={<MaterialIcons name="assignment" size={20} color="white" />}
        />
        <View
          style={[
            styles.optionGrid,
            styles.optionCardWrapper,
            isDarkMode && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
        >
          {options.map((option) => (
            <LHOptionCard
              key={option.id}
              title={option.questionText}
              selected={selectedOptions.some((item) => item.id === option.id)}
              onPress={() => handleToggleOption(option)}
              pdfLink={option.material}
              disabled={handoverState === "completed" || forceDisabled}
              onCameraPress={() => handleCameraPress(option)}
              hasUploadedImage={hasImages(option.id)}
              tagColor={isDarkMode ? colors.brand.primary : "#669DD7"}
              detailsText={option.details}
            />
          ))}
        </View>
        {/* Render the image upload modal */}
        {imageUploadModalVisible && selectedQuestion && (
          <ShiftHandoverActivityImageUpload
            visible={imageUploadModalVisible}
            onClose={handleCloseModal}
            questionId={selectedQuestion.id}
            questionText={selectedQuestion.questionText}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionHeader: {
    marginTop: 0,
    backgroundColor: '#3582E6',
    borderTopLeftRadius: 6, // Match PulseCheck header
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: 48, // Match PulseCheck header height
    justifyContent: 'center',
    alignItems: 'center',
  },
  header1text: {
    fontSize: 16, // Match PulseCheck header font size
    fontWeight: '700',
    color: '#fff',
    fontFamily: Platform.OS === 'ios' ? 'Inter-Bold' : 'Inter',
    textAlign: 'center',
    lineHeight: 20, // Match PulseCheck header line height
  },
  optionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: Platform.OS === 'web' ? 'flex-start' : 'space-between',
    gap: Platform.OS === 'web' ? 24 : 12,
    borderColor: '#e9ecef',
    borderWidth: 1,
    borderTopWidth: 0,
    borderRadius: 12,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: 24,
    backgroundColor: '#fff',
  },
  optionCardWrapper: {
    marginBottom: 0,
    backgroundColor: '#fff',
  },
  cardOuterContainer: {
    backgroundColor: '#F5F7FA',
    padding: 0,
    borderRadius: 12,
    shadowColor: '#005cbc',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 3,
    marginVertical: 12,
    width: '100%',
    alignSelf: 'center',
  },
  cardContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: Platform.OS === 'ios' ? 'visible' : 'hidden',
    width: '100%',
    alignSelf: 'center',
    elevation: 4,
    shadowColor: 'rgba(0, 94, 192, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    // Add shadow for web
    ...(Platform.OS === 'web' && {
      boxShadow: 'rgba(0, 94, 192, 0.08) 4px 0 24px -2px',
    }),
  },
});

export default ShiftHandOverActivity;
